import { Injectable, inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import {
  pipe,
  switchMap,
  tap,
  withLatestFrom,
  filter,
  map,
  debounce,
  debounceTime,
  combineLatest,
  of,
  max,
  skip,
} from 'rxjs';
import { Answer } from '../models/Answer.model';
import { AnswersApiService } from '../services/answers-api.service';
import { QuestionnaireStore } from 'src/app/store/questionnaire-store';
import { MatDialog } from '@angular/material/dialog';
import { SendSuccessComponent } from '../features/create-questionnaire-page/component/send-success/send-success.component';
import { Router } from '@angular/router';
import { GlobalStore } from './global-store';
import { EQuestionType } from '../models/questionType.model';
import { Question } from '../models/question.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { ModularQuestion } from '../models/modularQuestion.model';
import { TostesStore } from './tostes-store';
import { TostPositionEnum, TostTypeEnum } from '../shared/models/tostes.model';
import { QuestionnairesGridStore } from '../features/questionnaires/store/questionnairesGrid-store';
import { User } from '../models/user.model';
import { ApproverCandidate } from '../models/approverCandidate.model';
import { EApprovalStatus } from '../models/approval.model';
import { environment } from 'src/environment';

export interface validAnswer {
  readonly id: number;
  readonly statusAnswerValidation: StatusValidation;
  readonly questionId?: number;
}
export enum StatusValidation {
  Valid,
  InComplete,
  Error,
  NotValid,
}

export interface Validation {
  readonly arr: validAnswer[];
  readonly statusStepValidation: StatusValidation;
  readonly index: number;
  readonly id: number;
}
export interface AnswersState {
  readonly answers: Answer[];
  readonly loading: boolean;
  readonly isSent?: boolean;
  readonly loadingSave: boolean;
  readonly questionnaireId: number;
  readonly questionnaireGuid?: any;
  readonly currentStepIndex: number;
  readonly visitedSteps: number[];
  readonly stepsValidation: Validation[];
  readonly eventDeleteModularuestionId?: number;
  readonly showIndication?: boolean;
  readonly mainAnswer?: string;
  readonly approverAdmins?: ApproverCandidate[];
  readonly amount?: number;
  readonly currentAnswersUser?: User;
  readonly sentDate?: Date;
  readonly approvalDate?: Date;
  readonly recipientId?: number;
  readonly status?: EApprovalStatus;
  readonly isValid?: boolean;
  error?: string;
}

export class AnswersStore extends ComponentStore<AnswersState> {
  #answersApiService = inject(AnswersApiService);
  #questionnaireStore = inject(QuestionnaireStore);
  #questionnaireGridStore = inject(QuestionnairesGridStore);
  #globalStore = inject(GlobalStore);
  #dialog = inject(MatDialog);
  #router = inject(Router);
  #tostesStore = inject(TostesStore);

  currentStepQuestions$ = toSignal(
    this.#questionnaireStore.currentStepQuestions$,
  );
  constructor() {
    super({
      answers: [],
      loading: false,
      loadingSave: false,
      questionnaireId: 0,
      currentStepIndex: -1,
      visitedSteps: [],
      stepsValidation: [],
      approverAdmins: [],
    });

    this.questionnaireGuid$
      .pipe(filter((id) => id != null && id != undefined && id != 0))
      .subscribe((x) => this.loadAnswers());

    // this.#questionnaireStore.stepsLength$.subscribe(
    //   length => {
    //     let arr = [];
    //     for (let index = 0; index < length; index++) {
    //       arr.push({ arr: [], isValidStep: true, index })
    //     }
    //     this.updateStepsValidation(arr);
    //   }
    // )
    this.#questionnaireStore.currentStepIndex$.subscribe((index) => {
      this.updateCurrentStepsIndex(index);
    });

    this.answers$
      .pipe(
        withLatestFrom(this.loading$),
        filter(([answers, isLoad]) => !isLoad),
        debounceTime(1000),
      )
      .subscribe(([answers, isLoad]) => {
        if (answers && answers.length > 0) {
          this.saveAnswers();
        }
      });

    this.loading$.subscribe((load) =>
      this.#globalStore.updateLoadingAnswer(load),
    );

    combineLatest([
      this.answers$,
      this.#questionnaireStore.steps$,
      this.isValid$,
    ])
      .pipe(
        map(([answers, steps, isAllValid]) => {
          const x = steps.map((step, index) => {
            var answerValidations = [] as validAnswer[];
            step.questions
              .filter((q) => this.CheckIfTypeNeedValidation(q) == true)
              .forEach((question) => {
                var isRequired =
                  question.requierd == true ||
                  question.type.id == EQuestionType.Signature;
                var answer = answers.filter(
                  (ans) => ans.questionId == question.id,
                );
                if (answer.length > 0) {
                  let statusValidation = this.CheckValidationByType(
                    question,
                    answer,
                    isRequired,
                  );
                  answerValidations = [
                    ...answerValidations,
                    {
                      id: answers[0].id,
                      questionId: question.id,
                      statusAnswerValidation: statusValidation,
                    } as validAnswer,
                  ];
                  console.log('answerValidations', answerValidations);
                } else {
                  answerValidations = [
                    ...answerValidations,
                    {
                      id: 0,
                      questionId: question.id,
                      isValidAnswer: false,
                      statusAnswerValidation:
                        question.type.id == EQuestionType.Expense
                          ? StatusValidation.NotValid
                          : isRequired
                            ? StatusValidation.Error
                            : StatusValidation.InComplete,
                    } as validAnswer,
                  ];
                }
              });
            var stepValidation = {
              id: step.id,
              index,
              arr: answerValidations,
              statusStepValidation: answerValidations.some(
                (x) => x.statusAnswerValidation == StatusValidation.Error,
              )
                ? StatusValidation.Error
                : answerValidations.some(
                      (x) =>
                        x.statusAnswerValidation == StatusValidation.NotValid,
                    )
                  ? StatusValidation.NotValid
                  : answerValidations.every(
                        (x) =>
                          x.statusAnswerValidation == StatusValidation.Valid,
                      )
                    ? StatusValidation.Valid
                    : StatusValidation.InComplete,
            } as Validation;
            return stepValidation;
          });
          return x;
        }),
      )
      .subscribe((v) => this.updateStepsValidation(v));
  }

  readonly mainAnswer$ = this.select(({ mainAnswer }) => mainAnswer);
  readonly approverAdmins$ = this.select(
    ({ approverAdmins }) => approverAdmins,
  );
  readonly answers$ = this.select(({ answers }) => answers);
  readonly answersSig$ = toSignal(this.answers$);
  readonly isValid$ = this.select(({ isValid }) => isValid);
  readonly isAllValid$ = toSignal(this.select(({ isValid }) => isValid));
  readonly questionnaireId$ = this.select(
    ({ questionnaireId }) => questionnaireId,
  );
  readonly questionnaireGuid$ = this.select(
    ({ questionnaireGuid }) => questionnaireGuid,
  );
  readonly currentStepIndex$ = this.select(
    ({ currentStepIndex }) => currentStepIndex,
  );
  readonly visitedSteps$ = this.select(({ visitedSteps }) => visitedSteps);
  readonly eventDeleteModularuestionId$ = this.select(
    ({ eventDeleteModularuestionId }) => eventDeleteModularuestionId,
  );
  readonly loading$ = this.select(({ loading }) => loading);
  readonly loadingSave$ = this.select(({ loadingSave }) => loadingSave);
  readonly isSent$ = this.select(({ isSent }) => isSent);
  readonly validCurrentAllStep$ = this.select(
    ({ stepsValidation }) => stepsValidation,
  );
  readonly amount$ = this.select(({ amount }) => amount);
  readonly currentAnswersUser$ = this.select(
    ({ currentAnswersUser }) => currentAnswersUser,
  );
  readonly sentDate$ = this.select(({ sentDate }) => sentDate);
  readonly approvalDate$ = this.select(({ approvalDate }) => approvalDate);
  readonly recipientId$ = this.select(({ recipientId }) => recipientId);
  readonly status$ = this.select(({ status }) => status);
  readonly canEditClientChargeFields$ = this.select(
    ({ status }) => `${status}` === `${EApprovalStatus.PendingApproval}`,
  );
  readonly statusValidationCurrentStep$ = combineLatest([
    this.validCurrentAllStep$,
    this.currentStepIndex$,
  ]).pipe(
    map(([validAllSteps, index]) => {
      return validAllSteps.find((v) => v.index == index)?.statusStepValidation;
    }),
  );

  readonly showIndication$ = this.select(
    ({ showIndication }) => showIndication,
  );
  maxAmount = environment.featurs.amountExpense;

  CheckValidationByType(
    question: Question,
    answers: Answer[],
    isRequired: boolean,
  ): StatusValidation {
    var isFilled;

    switch (question.type.id) {
      case EQuestionType.Autocomplete:
      case EQuestionType.Mark:
        isFilled = answers[0].optionItemGuid != null;
        break;
      case EQuestionType.Fill:
      case EQuestionType.FreeText:
        isFilled = this.isNullOrEmptyString(answers[0].textAnswer);
        break;
      case EQuestionType.Date:
        isFilled = answers[0].dateAnswerNTZ != null;
        break;
      case EQuestionType.Number:
      case EQuestionType.Range:
        isFilled = answers[0].intAnswer != null;
        break;
      case EQuestionType.PhoneNumber:
        isFilled =
          this.isNullOrEmptyString(answers[0].textAnswer) &&
          this.isNullOrEmptyString(answers[0].multiOptionsAnswer);
        break;
      case EQuestionType.UploadFile:
      case EQuestionType.Signature:
        isFilled = this.isNullOrEmptyString(answers[0].base64);
        break;
      case EQuestionType.QuestionsGroup:
        {
          var validGroup = true;
          var maxOrder = Math.max(
            ...answers.map((ans) => ans.modularRowNum ?? 0),
          );
          for (let index = 1; index <= maxOrder; index++) {
            question.modularQuestions?.forEach((element) => {
              var ans = answers.find(
                (ans) =>
                  ans.modularQuestionGuid == element.guid &&
                  ans.modularRowNum == index,
              );
              if (ans) {
                validGroup =
                  validGroup && this.CheckValidationByModularType(element, ans);
              } else {
                validGroup = false;
              }
            });
          }
          isFilled = validGroup;
        }
        break;
      case EQuestionType.Table:
        isFilled = true;
        break;
      case EQuestionType.Multi:
        {
          let stringArray = answers[0].multiOptionsAnswer?.split(',');
          let currentGuidsArray = stringArray?.filter((n) =>
            question.optionsList?.answerOptionItem?.find((zn) => zn.guid == n),
          );
          isFilled = (currentGuidsArray?.length ?? 0) > 0;
        }
        break;
      case EQuestionType.Expense: {
        const expenses = answers[0]?.expense ?? [];

        // הוצאות חו"ל אינן כפופות לתקרת הסכום.
        const foreignExpenseGuids = new Set<string | null | undefined>(
          question.optionsList?.answerOptionItem
            ?.filter((o) => o.name === 'הוצאות חו"ל')
            .map((o) => o.guid),
        );
        const totalAmount = expenses
          .filter((expense) => !foreignExpenseGuids.has(expense.expenseType))
          .map((expense) => Number(expense.amount ?? 0))
          .reduce((sum, value) => sum + value, 0);

        const isTotalAmountValid = totalAmount <= this.maxAmount;

        return this.isAllValid$() && isTotalAmountValid
          ? StatusValidation.Valid
          : StatusValidation.NotValid;
      }
    }

    return isRequired && !isFilled
      ? StatusValidation.Error
      : isFilled
        ? StatusValidation.Valid
        : StatusValidation.InComplete;
  }
  IsShowFollowUpQuestion(currentQuestion: Question): boolean {
    var parentQuestion = this.currentStepQuestions$()?.find(
      (ans) => ans.id == currentQuestion.parentFollowUpQuestionId,
    );
    var parentAnswer = this.answersSig$()?.find(
      (ans) => ans.questionId == currentQuestion.parentFollowUpQuestionId,
    );
    if (
      parentQuestion?.type.id == EQuestionType.Autocomplete ||
      parentQuestion?.type.id == EQuestionType.Mark
    ) {
      return (
        parentAnswer?.optionItemGuid ==
        currentQuestion.followAnswerOptionItemGuid
      );
    }
    if (parentQuestion?.type.id == EQuestionType.Multi) {
      var y =
        parentAnswer?.multiOptionsAnswer?.includes(
          currentQuestion.followAnswerOptionItemGuid ??
            'eeeeeeeeeeeeeeeeeeeeeeeeeeeeee',
        ) ?? false;
      return y;
    }
    return false;
  }
  CheckIfTypeNeedValidation(question: Question): boolean {
    switch (question.type.id) {
      case EQuestionType.Title:
      case EQuestionType.Text:
      case EQuestionType.Image:
      case EQuestionType.UploadFileAdmin:
      case EQuestionType.Link:
      case EQuestionType.Divider:
        return false;
    }
    if (question.parentFollowUpQuestionId != null) {
      return this.IsShowFollowUpQuestion(question);
    }
    return true;
  }
  CheckValidationByModularType(
    modularQuestion: ModularQuestion,
    answer: Answer,
  ): boolean {
    switch (modularQuestion.type.id) {
      case EQuestionType.Autocomplete:
        return answer.optionItemGuid != null;
      case EQuestionType.Fill:
        return this.isNullOrEmptyString(answer.textAnswer);
      case EQuestionType.Date:
        return answer.dateAnswerNTZ != null;
      case EQuestionType.Number:
        return answer.intAnswer != null;
      case EQuestionType.PhoneNumber: {
        var x =
          this.isNullOrEmptyString(answer.textAnswer) &&
          this.isNullOrEmptyString(answer.multiOptionsAnswer);
        return x;
      }
      default:
        return true;
    }
  }
  isNullOrEmptyString(string?: string) {
    return string != '' && string != null && string.trim() != '';
  }

  readonly loadAnswers = this.effect<void>(
    pipe(
      withLatestFrom(this.questionnaireGuid$),
      tap(() => {
        this.patchState({ loading: true });
      }),
      switchMap(([_, guid]) =>
        this.#answersApiService.getAnswers(guid).pipe(
          tapResponse(
            (answersDetails) => {
              this.patchState({
                answers: answersDetails.answers,
                currentAnswersUser: answersDetails.user,
                sentDate: answersDetails.sentDate,
                approvalDate: answersDetails.approvalDate,
                recipientId: answersDetails.recipientId,
                loading: false,
              });
            },
            (error: Error) =>
              this.patchState({ error: error?.message, loading: false }),
          ),
        ),
      ),
    ),
  );

  readonly addAnswer = this.updater((state, answer: Answer) => ({
    ...state,
    answers: [...state.answers, answer],
  }));
  readonly resetDeleteModularQuestionId = this.updater((state) => ({
    ...state,
    eventDeleteModularuestionId: undefined,
  }));
  readonly updateShowIndication = this.updater(
    (state, showIndication: boolean) => ({
      ...state,
      showIndication,
    }),
  );
  readonly updateAnswer = this.updater((state, answer: Answer) => ({
    ...state,
    answers: [
      ...state.answers.filter(
        (ans) =>
          ans.questionId != answer.questionId ||
          ans.modularQuestionGuid != answer.modularQuestionGuid ||
          ans.modularRowNum != answer.modularRowNum,
      ),
      answer,
    ],
  }));
  readonly updateQuestionnaireId = this.updater((state, id: number) => ({
    ...state,
    questionnaireId: id,
  }));
  readonly updateQuestionnaireGuid = this.updater((state, guid: any) => ({
    ...state,
    questionnaireGuid: guid,
  }));
  readonly updateStepsValidation = this.updater((state, arr: Validation[]) => ({
    ...state,
    stepsValidation: arr,
  }));
  readonly updateCurrentStepsIndex = this.updater((state, arr: any) => ({
    ...state,
    currentStepIndex: arr,
  }));

  readonly updateVisitedSteps = this.updater((state, step: number) => {
    const updatedVisitedSteps = state.visitedSteps.filter((s) => s !== step);
    return {
      ...state,
      visitedSteps: [...updatedVisitedSteps, step],
    };
  });

  readonly deleteAnswerById = this.updater((state, questionId: number) => ({
    ...state,
    answers: [...state.answers.filter((ans) => ans.questionId != questionId)],
  }));

  readonly deleteAnswerByIdAndRow = this.updater(
    (state, payload: { id: number; row: number }) => {
      console.log('deleteAnswerByIdAndRow - payload:', payload);
      console.log('deleteAnswerByIdAndRow - answers BEFORE:', state.answers);

      const filteredAnswers = state.answers.filter((ans) => {
        const shouldDelete =
          ans.questionId === payload.id && ans.modularRowNum === payload.row;

        if (shouldDelete) {
          console.log('Deleting answer:', ans);
        }

        return !shouldDelete;
      });

      console.log(
        'deleteAnswerByIdAndRow - answers AFTER FILTER:',
        filteredAnswers,
      );

      const updatedAnswers = filteredAnswers.map((ans) => {
        const shouldDecreaseRow =
          ans.questionId === payload.id &&
          ans.modularRowNum != null &&
          ans.modularRowNum > payload.row;

        if (shouldDecreaseRow) {
          console.log('Updating modularRowNum:', {
            before: ans,
            oldRow: ans.modularRowNum,
            newRow: ans.modularRowNum - 1,
          });
        }

        return {
          ...ans,
          id: shouldDecreaseRow ? 0 : ans.id,
          modularRowNum: shouldDecreaseRow
            ? ans.modularRowNum - 1
            : ans.modularRowNum,
        };
      });

      console.log('deleteAnswerByIdAndRow - answers FINAL:', updatedAnswers);

      return {
        ...state,
        answers: updatedAnswers,
      };
    },
  );

  readonly saveAnswers = this.effect<void>(
    pipe(
      tap(() => this.patchState({ loadingSave: true })),
      withLatestFrom(
        this.answers$,
        this.questionnaireGuid$,
        this.isSent$,
        this.mainAnswer$,
      ),
      filter(([_, q, x, isSent]) => !isSent),
      switchMap(([_, ans, guid, isSent, mainAnswer]) =>
        this.#answersApiService.saveAnswers(guid, ans, isSent, mainAnswer).pipe(
          tapResponse(
            (response) => this.patchState({ loadingSave: false }),
            (error: Error) => {
              this.#tostesStore.addTost({
                guid: this.#tostesStore.generateGuid(),
                message: {
                  en: 'An error occurred while saving the answers',
                  he: 'ארעה שגיאה בשמירת התשובות',
                },
                position: TostPositionEnum.BottomLeft,
                seconds: 5,
                type: TostTypeEnum.ERROR,
              });
              this.patchState({ error: error?.message, loadingSave: false });
            },
          ),
        ),
      ),
    ),
  );
  readonly deleteAnswerModularGropByOrder = this.effect<{
    order: number;
    questionId: number;
  }>(
    pipe(
      tap((detailsModular) => this.patchState({ loadingSave: true })),
      withLatestFrom(this.answers$, this.questionnaireGuid$),
      map(([detailsModular, answers, guid]) => {
        return {
          detailsModular,
          answers: answers
            .filter(
              (ans) =>
                ans.questionId != detailsModular.questionId ||
                ans.modularRowNum != detailsModular.order,
            )
            .map((ans) => {
              if (ans.questionId == detailsModular.questionId) {
                if (
                  ans.modularRowNum != undefined &&
                  ans.modularRowNum > detailsModular.order
                ) {
                  return {
                    ...ans,
                    modularRowNum: ans.modularRowNum - 1,
                    id: 0,
                  };
                } else {
                  return ans;
                }
              } else {
                return ans;
              }
            }),
          guid,
        };
      }),
      tap((props) => {
        this.patchState({
          answers: [...props.answers],
          eventDeleteModularuestionId: props.detailsModular.questionId,
        });
        this.patchState({
          eventDeleteModularuestionId: undefined,
        });
      }),
      switchMap((props) =>
        this.#answersApiService
          .deleteModularRow(
            props.detailsModular.questionId,
            props.detailsModular.order,
            props.guid,
          )
          .pipe(
            tapResponse(
              (response) => this.patchState({ loadingSave: false }),
              (error: Error) => {
                // this.resetDeleteModularQuestionId();
                this.patchState({ error: error?.message, loadingSave: false });
              },
            ),
          ),
      ),
    ),
  );
  readonly sendAnswers = this.effect<{
    approverId1?: number;
    groupId1?: number;
    approverId2?: number;
    groupId2?: number;
  } | void>(
    pipe(
      tap(() => this.patchState({ loadingSave: true, isSent: true })),
      withLatestFrom(
        this.answers$,
        this.questionnaireGuid$,
        this.mainAnswer$,
        this.amount$,
      ),
      // filter(([_,q])=>q!=undefined),
      switchMap(([selectedAdmins, ans, guid, mainAnswer, amount]) =>
        this.#answersApiService
          .saveAnswers(
            guid,
            ans,
            true,
            mainAnswer,
            selectedAdmins || undefined,
            amount,
          )
          .pipe(
            tapResponse(
              (response) => {
                this.#globalStore.refreshQuestionnaireAfterUpdate();
                this.patchState({ loadingSave: false });
                this.#dialog
                  .open(SendSuccessComponent, {
                    position: { top: '8%' },
                    panelClass: 'dialog-panel',
                    data: { isFromPublishAnswers: true },
                  })
                  .afterClosed()
                  .subscribe((x) => {
                    if (!x) {
                      this.#router.navigate(['main']);
                    }
                  });
              },
              (error: Error) => {
                this.patchState({ error: error?.message, loadingSave: false });
                this.#tostesStore.addTost({
                  guid: this.#tostesStore.generateGuid(),
                  message: {
                    en: 'An error occurred while  sending the answers',
                    he: 'ארעה שגיאה בשליחת התשובות',
                  },
                  position: TostPositionEnum.BottomLeft,
                  seconds: 5,
                  type: TostTypeEnum.ERROR,
                });
              },
            ),
          ),
      ),
    ),
  );

  readonly addUnauthorizedUrl = this.effect<number>(
    pipe(
      switchMap((questionId) =>
        this.#answersApiService.addUnauthorizedUrl(questionId),
      ),
      tapResponse(
        (response) => {
          console.log('unauthorized url', response);
          this.#tostesStore.addTost({
            guid: this.#tostesStore.generateGuid(),
            message: {
              en: 'Unauthorized URL added successfully',
              he: 'הקישור ללא הרשאה נוסף בהצלחה',
            },
            position: TostPositionEnum.BottomLeft,
            seconds: 5,
            type: TostTypeEnum.SUCCESS,
          });
        },
        (error: Error) => {
          this.#tostesStore.addTost({
            guid: this.#tostesStore.generateGuid(),
            message: {
              en: 'Failed to add unauthorized URL',
              he: 'הוספת הקישור ללא הרשאה נכשלה',
            },
            position: TostPositionEnum.BottomLeft,
            seconds: 5,
            type: TostTypeEnum.ERROR,
          });
        },
      ),
    ),
  );

  readonly updateMainAnswer = this.updater((state, mainAnswer: string) => ({
    ...state,
    mainAnswer,
  }));

  readonly updateMainAnswerAndSync = this.effect<string>((mainAnswer$) =>
    mainAnswer$.pipe(
      tap((mainAnswer) => this.updateMainAnswer(mainAnswer)),
      withLatestFrom(this.questionnaireGuid$),
      tap(([mainAnswer, questionnaireGuid]) => {
        this.#questionnaireGridStore.updateMainAnswer({
          questionnaireGuid,
          mainAnswer,
        });
      }),
    ),
  );

  readonly fetchApproverAdmins = this.effect<number>(
    pipe(
      switchMap((questionnaireId) =>
        this.#answersApiService.getApproversAdmins(questionnaireId).pipe(
          tapResponse(
            (users) => {
              this.patchState({ approverAdmins: users });
            },
            (error: any) => {
              console.error(error);
            },
          ),
        ),
      ),
    ),
  );

  readonly updateAmount = this.updater((state, amount: number) => ({
    ...state,
    amount,
  }));

  readonly updateApprovalMode = this.updater(
    (state, status: EApprovalStatus) => ({
      ...state,
      status: status,
    }),
  );
  readonly defaultApprovedAmounts = this.effect<Answer>(
    pipe(
      debounceTime(0),
      tap((answer) => this.updateAnswer(answer)),
    ),
  );

  readonly updateAnswerIsValid = this.updater((state, isValid: boolean) => ({
    ...state,
    isValid,
  }));
}
